import IProduct from "models/sql/IProduct";
import { useContext, useEffect, useRef, useState } from "react";
import { Button, Card, Col, Form, Modal, Row, Table } from "react-bootstrap";
import { GlobalContext } from "components/GlobalContext";
import ProductService from "services/ProductService";
import { formatCurrency, formatDate } from "utilities/StringUtility";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faTrash } from "@fortawesome/free-solid-svg-icons";

import './styles/ProductPricingCalculator.scss';
import { printElement } from "utilities/BrowserUtility";
import IDealer from "models/sql/IDealer";
import ISalesRep from "models/sql/ISalesRep";
import MiscService from "services/MiscService";

export interface IProductPricingCalculatorProps {
    className?: string;
    title?: string;
    dealer?: IDealer;
    includeDiscounts?: boolean;
    rep?: ISalesRep;
    onChange?: (productList: IProductListEntry[]) => void;
    onTotalChange?: (total: number, productList: IProductListEntry[], hasChanged?: boolean) => void;
    initialProductList?: IProductListEntry[];
    hidePrintButton?: boolean;
    hideEmailButton?: boolean;
}

export interface IProductListEntry {
    productId: number;
    quantity: number;
    total: number;
}

const ProductPricingCalculator = (props: IProductPricingCalculatorProps) => {
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const [products, setProducts] = useState<IProduct[]>([]);
    const [productList, setProductList] = useState<IProductListEntry[]>([]);
    const [showAddProductModal, setShowAddProductModal] = useState(false);
    const [addProductId, setAddProductId] = useState(0);
    const [total, setTotal] = useState(0);
    const [refreshTotals, setRefreshTotals] = useState(new Date());
    const [hasChanged, setHasChanged] = useState(false);

    useEffect(
        () => {
            (async () => {
                globalContextRef.current.setLoading(true);
                const accessToken = await globalContext.getAccessTokenAsync();
                const result =
                    await ProductService.GetAllAsync(
                        accessToken,
                        0,
                        999,
                        {
                            published: true
                        });

                if (result.records != null) {
                    setProducts(result.records);
                    setRefreshTotals(new Date());
                }
                else {
                    globalContextRef.current.showErrorMessage("Unable to load Products");
                }

                globalContextRef.current.setLoading(false);
            })();
        }, []);

    useEffect(() => {
        setProductList(props.initialProductList ?? []);
        setRefreshTotals(new Date());
    }, [props.initialProductList]);

    useEffect(
        () => {
            productList.forEach(p => {
                const product = products.find(a => a.productId === p.productId);
                if (product && product.listPrice) {
                    var finalAdjustment = 1;

                    if (props.includeDiscounts && props.dealer) {
                        if (product.productType == "Equipment" && props.dealer.airEquipmentDiscount) {
                            finalAdjustment = 1 - (props.dealer.airEquipmentDiscount / 100);
                        }
                        else if (product.productType == "Consumable" && props.dealer.airConsumableDiscount) {
                            finalAdjustment = 1 - (props.dealer.airConsumableDiscount / 100);
                        }
                    }

                    p.total = (product.listPrice * finalAdjustment) * p.quantity;
                }
                else {
                    p.total = 0;
                }
            });

            const total = productList.reduce((a, b) => a + b.total, 0);
            setTotal(total);

            if (props.onTotalChange) {
                props.onTotalChange(total, productList, hasChanged);
            }
        },
        [refreshTotals, productList]);

    const emailAsync = async (toAddress: string) => {
        globalContextRef.current.setLoading(true);
        let headContent = `<h1>Holmatro USCA Growth Portal Pricing Estimate</h1><i>Generated on ${formatDate((new Date()))} by ${globalContextRef.current.userName}</i><br/>`;
        let tableContent = `<table><tr><td>Product</td><td>Quantity</td></tr>`;

        productList.forEach((p, index) => {
            const product = products.find(a => a.productId === p.productId);
            if (!product) {
                return;
            }

            tableContent += `<tr><td>${product.name}</td><td>${p.quantity} x ${formatCurrency(product?.listPrice || 0)} = ${formatCurrency(p.total)}</td></tr>`;
        });

        tableContent += `</table>`;
        const emailDisclaimer = `<strong>*Disclaimer:</strong> This is presented for convenience only, actual pricing is subject to change. Please contact us for a formal quote.`;
        const emailBody = `${headContent}<br/>${tableContent}<br/><br/>${emailDisclaimer}`;

        const accessToken = await globalContext.getAccessTokenAsync();
        await MiscService.sendEmailRawAsync(
            accessToken,
            toAddress,
            "NoReply@HolmatroUSCA.com",//globalContextRef.current.userEmail,
            "Holmatro USCA Growth Portal Pricing Estimate",
            emailBody);

        globalContextRef.current.showModal({
            content: <div className="text-center">Email sent to {toAddress}</div>
        });

        globalContextRef.current.setLoading(false);
    };

    const hideAddProductModal = () => {
        setShowAddProductModal(false);
        setAddProductId(0);
    };

    const popupAddProductModal = () => {
        setShowAddProductModal(true);
        const firstProduct = products.filter(p => !productList.find(a => a.productId == p.productId))[0];
        setAddProductId(firstProduct.productId);
    };

    const addProduct = () => {
        const newProductList = [
            ...productList,
            {
                productId: addProductId,
                quantity: 1,
                total: 0
            } as IProductListEntry
        ];

        setProductList(newProductList);
        setHasChanged(true);

        if (props.onChange) {
            props.onChange(newProductList);
        }

        hideAddProductModal();
        setRefreshTotals(new Date());
    };

    const removeProduct = (product: IProduct) => {
        productList.splice(
            productList.findIndex(p => p.productId === product.productId),
            1);

        setProductList([...productList]);
        setHasChanged(true);

        if (props.onChange) {
            props.onChange(productList);
        }
        setRefreshTotals(new Date());
    };

    return (
        <>
            <Card className={props.className}>
                <Card.Body>
                    <Row className="align-items-end">
                        <Col>
                            <Card.Title>
                                {props.title ?? "Pricing Calculator"}
                                {
                                    props.hidePrintButton !== true &&
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        className="m-1 float-end"
                                        style={{ position: "relative", top: "-8px" }}
                                        onClick={() => {
                                            printElement(document.querySelector(".printout-container"));
                                        }}>
                                        Print
                                    </Button>
                                }
                                {
                                    props.hideEmailButton !== true &&
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        className="m-1 float-end"
                                        style={{ position: "relative", top: "-8px" }}
                                        onClick={() => {
                                            emailAsync(globalContextRef.current.userEmail);
                                        }}>
                                        Email
                                    </Button>
                                }
                                {/*
                                <Button
                                    variant="primary"
                                    size="sm"
                                    className="m-1 float-end"
                                    style={{ position: "relative", top: "-8px" }}
                                    onClick={() => {
                                        globalContext.showModal({
                                            content:
                                                <></>,
                                            title: "Build Link",
                                            formElements: [
                                                {
                                                    key: "email",
                                                    title: "Send to email address",
                                                    type: "text",
                                                    required: true,
                                                    value: ""
                                                }
                                            ],
                                            okCallback:
                                                async (data) => {
                                                    const toAddress = data?.[0].value;
                                                    if (toAddress) {
                                                        emailAsync(toAddress);
                                                    }
                                                },
                                            cancelCallback: () => { }
                                        });
                                    }}>
                                    Email
                                </Button>
                                */}
                            </Card.Title>
                        </Col>
                    </Row>
                    <Row className="align-items-end">
                        <Col>
                            <Table striped bordered hover size="sm" className="">
                                <thead>
                                    <tr>
                                        <td><strong>Product</strong></td>
                                        <td><strong>Quantity</strong></td>
                                        <td><strong>Total</strong></td>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        productList.map((p, index) => {
                                            const product = products.find(a => a.productId === p.productId);
                                            if (!product) {
                                                return <></>;
                                            }

                                            return (
                                                <tr key={p.productId}>
                                                    <td>
                                                        <span
                                                            style={{ cursor: "pointer" }}
                                                            className="pe-1"
                                                            title="Remove Product"
                                                            onClick={(e) => removeProduct(product)}><FontAwesomeIcon icon={faTrash as IconProp} /></span>
                                                        {product.name}
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            type="text"
                                                            onChange={
                                                                (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                    p.quantity = parseInt(e.currentTarget.value) || 0;
                                                                    setHasChanged(true);
                                                                    setProductList([...productList]);
                                                                    setRefreshTotals(new Date());
                                                                }
                                                            }
                                                            value={p.quantity}
                                                        />
                                                    </td>
                                                    <td>{formatCurrency(p.total)}</td>
                                                </tr>
                                            )
                                        })
                                    }
                                    <tr>
                                        <td></td>
                                        <td className="text-end"><strong>Estimated Total</strong></td>
                                        <td>
                                            <strong>{formatCurrency(total)}</strong>
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>
                            <div className="text-center">
                                <Button
                                    variant="primary"
                                    onClick={popupAddProductModal}>
                                    Add Product
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Modal show={showAddProductModal} id="lookupModal" onHide={hideAddProductModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Add Product</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3" >
                            <Form.Label>Priority</Form.Label>
                            <Form.Select
                                onChange={async (e) => {
                                    console.log(parseInt(e.currentTarget.value));
                                    setAddProductId(parseInt(e.currentTarget.value));
                                }}
                                value={addProductId}>
                                {
                                    products
                                        .filter(p => !productList.find(a => a.productId == p.productId))
                                        .map(
                                            (s, index) =>
                                                <option key={s.productId} value={s.productId}>{s.name}</option>
                                        )
                                }
                            </Form.Select>
                        </Form.Group>
                    </Form>
                    <div>
                        {
                            products.filter(p => p.productId === addProductId).map(
                                p =>
                                    <div
                                        key={p.productId}
                                        className="product-description text-center">
                                        {
                                            p.imagePath &&
                                            <>
                                                <img style={{ width: "50%" }} src={p.imagePath} /><br /><br />
                                            </>
                                        }
                                        {
                                            p.shortDescription &&
                                            <span>
                                                {p.shortDescription}
                                            </span>
                                        }

                                    </div>)
                        }
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={hideAddProductModal}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={addProduct}>
                        Add
                    </Button>
                </Modal.Footer>
            </Modal>

            <div className="printout-container" style={{ display: "none", fontFamily: "arial" }}>
                <Row>
                    <Col>
                        <h1 style={{ lineHeight: "0.7em", paddingTop: "10px" }}>
                            Holmatro USCA Growth Portal Pricing Estimate<br />
                            <small style={{ fontSize: "15px" }}><i>Generated on {formatDate((new Date()))} by {globalContextRef.current.userName} ({globalContextRef.current.userEmail})</i></small>
                        </h1>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Table striped bordered hover size="sm" className="" style={{ fontSize: "25px" }}>
                            <tbody>
                                {
                                    productList.map((p, index) => {
                                        const product = products.find(a => a.productId === p.productId);
                                        if (!product) {
                                            return <></>;
                                        }

                                        return (
                                            <tr key={p.productId}>
                                                <td>
                                                    {product.name}
                                                </td>
                                                <td>
                                                    {p.quantity} x {formatCurrency(product?.listPrice || 0)} = {formatCurrency(p.total)}
                                                </td>
                                            </tr>
                                        )
                                    })
                                }
                                <tr>
                                    <td colSpan={2}>
                                        <hr />
                                    </td>
                                </tr>
                                <tr>
                                    <td><strong>Estimated Total</strong></td>
                                    <td>
                                        <strong>{formatCurrency(total)}</strong>
                                    </td>
                                </tr>
                            </tbody>
                        </Table>
                        <p>
                            <strong>*Disclaimer:</strong> This is presented for convenience only, actual pricing is subject to change. Please contact us for a formal quote.
                        </p>
                    </Col>
                </Row>

            </div>
        </>
    );
}

export default ProductPricingCalculator;